import { createContext, useContext, useState, useEffect } from 'react';
import { ThemeProvider } from 'styled-components';
import { lightTheme, darkTheme } from '../Styles/theme';

const ThemeContext = createContext();
const themeChannel = new BroadcastChannel('theme-preview');
const VALID_MODES = ['light', 'dark'];

function getSavedMode() {
  const saved = window.localStorage.getItem('isDarkMode');
  return VALID_MODES.includes(saved) ? saved : 'light';
}

export function ThemeContextProvider({ children }) {
  const [isDarkMode, setIsDarkMode] = useState(getSavedMode);

  // 다른 탭에서 보낸 미리보기 수신
  useEffect(() => {
    const handler = (e) => {
      if (e.data?.type === 'preview') setIsDarkMode(e.data.mode);
    };
    themeChannel.addEventListener('message', handler);
    return () => themeChannel.removeEventListener('message', handler);
  }, []);

  // 저장 없이 화면만 미리보기 (다른 탭에도 브로드캐스트)
  const previewIsDark = (mode) => {
    setIsDarkMode(mode);
    themeChannel.postMessage({ type: 'preview', mode });
  };

  // 현재 상태를 localStorage에 저장 (저장하기 버튼용)
  const setIsDark = () => window.localStorage.setItem('isDarkMode', isDarkMode);

  // 상태 변경 + localStorage 저장 + 다른 탭 동기화 (저장하기 버튼용 - 즉시 적용)
  const applyIsDark = (mode) => {
    setIsDarkMode(mode);
    window.localStorage.setItem('isDarkMode', mode);
    themeChannel.postMessage({ type: 'preview', mode });
  };

  // localStorage 값으로 롤백 (저장 안 하고 이동 시)
  const revertIsDark = () => previewIsDark(getSavedMode());

  const isDark = isDarkMode === 'dark';

  // App 바깥 영역(html/body/#root)도 현재 테마 색상으로 동기화
  useEffect(() => {
    const backgroundColor = isDark ? darkTheme.background : lightTheme.background;

    document.documentElement.style.backgroundColor = backgroundColor;
    document.body.style.backgroundColor = backgroundColor;

    const rootElement = document.getElementById('root');
    if (rootElement) rootElement.style.backgroundColor = backgroundColor;
  }, [isDark]);

  return (
    <ThemeContext.Provider value={{ isDark, previewIsDark, setIsDark, applyIsDark, revertIsDark }}>
      <ThemeProvider theme={isDark ? darkTheme : lightTheme}>
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
